import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import {
  Route,
  BetweenVerticalStart,
  Tags,
  Bug,
  Mail,
} from 'lucide-react';
import { IconAsset, IconCertificate, IconLayoutCollage } from '@tabler/icons-react';
import ConfigSectionSidebarEdge from '../config-hub/ConfigSectionSidebarEdge';
import './ProcessConfigSidebar.css';

function OficinaSectionIcon(props) {
  const { strokeWidth = 1.75, ...rest } = props;
  return <IconAsset stroke={strokeWidth} {...rest} />;
}

function ProcessosExibicoesIcon(props) {
  const { strokeWidth = 1.75, ...rest } = props;
  return <IconLayoutCollage stroke={strokeWidth} {...rest} />;
}

function EspecialidadeSubmenuIcon(props) {
  const { strokeWidth = 1.75, ...rest } = props;
  return <IconCertificate stroke={strokeWidth} {...rest} />;
}

const PROCESS_SUBMENU_SECTIONS = [
  {
    title: null,
    items: [
      { id: 'config-status-2', label: 'Configuração de status', Icon: Route },
      { id: 'coluna-em-aberto', label: 'Configuração de colunas', Icon: BetweenVerticalStart },
      { id: 'condicoes-intervencao', label: 'Condições de intervenção', Icon: Tags },
      { id: 'defeitos-verve', label: 'Serviços', Icon: Bug },
      { id: 'cadastros-marcas-subgrupos', label: 'Especialidade técnica', Icon: EspecialidadeSubmenuIcon },
      { id: 'notificacoes-email', label: 'Notificações por email', Icon: Mail },
    ],
  },
  {
    title: 'Oficina',
    TitleIcon: OficinaSectionIcon,
    items: [
      { id: 'oficina', label: 'Processos e exibições', Icon: ProcessosExibicoesIcon },
    ],
  },
];

function ProcessSidebarTooltipPortal({ anchorRect, text }) {
  if (!anchorRect || !text) return null;

  return createPortal(
    <span
      className="process-sidebar-tooltip-portal"
      style={{
        top: anchorRect.top + anchorRect.height / 2,
        left: anchorRect.right + 10,
      }}
      role="tooltip"
    >
      {text}
    </span>,
    document.body
  );
}

function useProcessSidebarTooltip(text) {
  const anchorRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const showTooltip = useCallback(() => {
    if (!text) return;
    const el = anchorRef.current;
    if (!el) return;
    setAnchorRect(el.getBoundingClientRect());
  }, [text]);

  const hideTooltip = useCallback(() => {
    setAnchorRect(null);
  }, []);

  return { anchorRef, anchorRect, showTooltip, hideTooltip };
}

function ProcessSubmenuItem({ id, label, Icon, active, collapsed, onSelect }) {
  const labelRef = useRef(null);
  const [truncatedHover, setTruncatedHover] = useState(false);

  const showTooltip = collapsed || truncatedHover;
  const { anchorRef, anchorRect, showTooltip: openTooltip, hideTooltip } = useProcessSidebarTooltip(label);

  const handleMouseEnter = useCallback(() => {
    if (collapsed) {
      openTooltip();
      return;
    }
    const el = labelRef.current;
    const isTruncated = Boolean(el && el.scrollWidth > el.clientWidth + 1);
    setTruncatedHover(isTruncated);
    if (isTruncated) openTooltip();
  }, [collapsed, openTooltip]);

  const handleMouseLeave = useCallback(() => {
    setTruncatedHover(false);
    hideTooltip();
  }, [hideTooltip]);

  const setButtonRef = useCallback(
    (node) => {
      anchorRef.current = node;
    },
    [anchorRef]
  );

  return (
    <>
      <button
        ref={setButtonRef}
        type="button"
        className={['process-submenu-item', active ? 'active' : ''].filter(Boolean).join(' ')}
        onClick={() => onSelect(id)}
        aria-label={collapsed ? label : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        <Icon size={17} strokeWidth={1.75} aria-hidden />
        <span ref={labelRef} className="process-submenu-label">
          {label}
        </span>
      </button>
      <ProcessSidebarTooltipPortal anchorRect={showTooltip ? anchorRect : null} text={label} />
    </>
  );
}

export default function ProcessConfigSidebar({ activeSubmenu, onSelectSubmenu }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <aside
      className={`process-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`}
      aria-label="Processos — configurações"
    >
      <ConfigSectionSidebarEdge
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
      />
      <nav className="process-submenu">
        {PROCESS_SUBMENU_SECTIONS.map((section, sectionIdx) => (
          <div
            key={section.title || `section-${sectionIdx}`}
            className="process-submenu-section"
          >
            {section.title ? (
              <div
                className="process-submenu-section-title"
                aria-hidden={sidebarCollapsed}
              >
                {section.TitleIcon ? (
                  <section.TitleIcon size={18} strokeWidth={1.75} aria-hidden />
                ) : null}
                <span>{section.title}</span>
              </div>
            ) : null}
            {section.items.map((item) => (
              <ProcessSubmenuItem
                key={item.id}
                {...item}
                active={activeSubmenu === item.id}
                collapsed={sidebarCollapsed}
                onSelect={onSelectSubmenu}
              />
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
